import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  BbotButton,
  BbotModal,
  Checkbox,
  Input,
  PageLoadingSpinner,
  Radio,
  Select,
  Space,
} from "top-component-library";
import DisconnectIntegrationDialog from "./DisconnectIntegrationDialog";
import { ACTION_ON_FAILURE_OPTIONS, ACTION_ON_SUCCESS_OPTIONS } from "./constants";
import { FormRow } from "./styles";

const LOCATION_ID_DEBOUNCE_MS = 200;

// Shared by Omnivore Direct and Omnivore 3 (add-integration-dialog.html): both are the
// same location-id-driven lookup into employee/revenue-center/order-type/tender-type
// selects, differing only in sync_price_levels (3 only), diagnostics (Direct only, 3 has
// none in legacy either), the successful-injection heading text, and the order-type
// option's label format. Reachable only once already connected, same reasoning as
// OmnivoreMmsDialog for the diagnostics/disconnect gating.
const OmnivoreConnectDialog = ({
  visible,
  variant,
  isAdmin,
  settings,
  locationOptions,
  isLoadingLocationOptions,
  onClose,
  onLoad,
  onLocationIdChange,
  onSave,
  onDisconnect,
  onCheckDiagnostic,
  onDownloadPosItems,
}) => {
  const [localSettings, setLocalSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [disconnectVisible, setDisconnectVisible] = useState(false);
  const debounceRef = useRef(null);
  // Tracks which settings object has already been seeded, so a reopen can't briefly show
  // (and let Submit act on) the hook's still-stale cache from before this open's onLoad()
  // resolves - localSettings is cleared to null on every open and only repopulated once a
  // genuinely new settings reference arrives.
  const consumedSettingsRef = useRef(null);

  const displayName = variant === "omnivore3" ? "Omnivore" : "Omnivore Direct";

  useEffect(() => {
    if (visible) {
      setLocalSettings(null);
      onLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (visible && settings && settings !== consumedSettingsRef.current) {
      consumedSettingsRef.current = settings;
      setLocalSettings(settings);
      if (settings.location_id) onLocationIdChange(settings.location_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, settings]);

  const setField = (field, value) => setLocalSettings((current) => ({ ...current, [field]: value }));

  const handleLocationIdChange = (value) => {
    setField("location_id", value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onLocationIdChange(value), LOCATION_ID_DEBOUNCE_MS);
  };

  const handleSave = async () => {
    setSaving(true);
    const succeeded = await onSave(localSettings);
    setSaving(false);
    if (succeeded) onClose();
  };

  const requiredFieldsMissing =
    !localSettings?.location_id ||
    !localSettings?.employee_id ||
    !localSettings?.revenue_center_id ||
    !localSettings?.order_type_id ||
    !localSettings?.tender_type_id;

  return (
    <BbotModal
      title={`${displayName} Integration Settings`}
      visible={visible}
      onCancel={onClose}
      footer={[
        isAdmin && (
          <BbotButton key="disconnect" danger onClick={() => setDisconnectVisible(true)}>
            Disconnect
          </BbotButton>
        ),
        <BbotButton key="submit" type="primary" loading={saving} disabled={requiredFieldsMissing} onClick={handleSave}>
          Submit
        </BbotButton>,
        <BbotButton key="close" onClick={onClose}>
          Close
        </BbotButton>,
      ]}
    >
      {!localSettings ? (
        <PageLoadingSpinner />
      ) : (
        <div>
          <FormRow>
            <label htmlFor="omnivore-connect-location-id">Omnivore Location Id</label>
            <Input
              id="omnivore-connect-location-id"
              value={localSettings.location_id}
              onChange={(event) => handleLocationIdChange(event.target.value)}
            />
          </FormRow>
          {isLoadingLocationOptions ? (
            <PageLoadingSpinner />
          ) : (
            <>
              <FormRow>
                <label htmlFor="omnivore-connect-employee">Employee</label>
                <Select
                  id="omnivore-connect-employee"
                  value={localSettings.employee_id ?? undefined}
                  onChange={(value) => setField("employee_id", value)}
                >
                  {(locationOptions?.employees ?? []).map((employee) => (
                    <Select.Option key={employee.id} value={employee.id}>
                      {/* Deviation from legacy: add-integration-dialog.html raw-interpolates
                          '{{ first }} {{ middle }} {{ last }}', leaving a double space when
                          middle_name is empty. filter(Boolean) drops the gap instead. */}
                      {[employee.first_name, employee.middle_name, employee.last_name].filter(Boolean).join(" ")}
                    </Select.Option>
                  ))}
                </Select>
              </FormRow>
              <FormRow>
                <label htmlFor="omnivore-connect-revenue-center">Revenue Center</label>
                <Select
                  id="omnivore-connect-revenue-center"
                  value={localSettings.revenue_center_id ?? undefined}
                  onChange={(value) => setField("revenue_center_id", value)}
                >
                  {(locationOptions?.revenue_centers ?? []).map((revenueCenter) => (
                    <Select.Option key={revenueCenter.id} value={revenueCenter.id}>
                      {revenueCenter.name}
                    </Select.Option>
                  ))}
                </Select>
              </FormRow>
              <FormRow>
                <label htmlFor="omnivore-connect-order-type">Order Type</label>
                <Select
                  id="omnivore-connect-order-type"
                  value={localSettings.order_type_id ?? undefined}
                  onChange={(value) => setField("order_type_id", value)}
                >
                  {(locationOptions?.order_types ?? []).map((orderType) => (
                    <Select.Option key={orderType.id} value={orderType.id}>
                      {variant === "omnivore3" ? `${orderType.name} (${orderType.id})` : orderType.name}
                    </Select.Option>
                  ))}
                </Select>
              </FormRow>
              <FormRow>
                <label htmlFor="omnivore-connect-tender-type">Tender Type</label>
                <Select
                  id="omnivore-connect-tender-type"
                  value={localSettings.tender_type_id ?? undefined}
                  onChange={(value) => setField("tender_type_id", value)}
                >
                  {(locationOptions?.tender_types ?? []).map((tenderType) => (
                    <Select.Option key={tenderType.id} value={tenderType.id}>
                      {tenderType.name}
                    </Select.Option>
                  ))}
                </Select>
              </FormRow>
            </>
          )}
          <Checkbox
            checked={Boolean(localSettings.sync_in_stock)}
            onChange={(event) => setField("sync_in_stock", event.target.checked)}
          >
            Sync &apos;In Stock&apos; from POS (if compatible)
          </Checkbox>
          {variant === "omnivore3" && (
            <Checkbox
              checked={Boolean(localSettings.sync_price_levels)}
              onChange={(event) => setField("sync_price_levels", event.target.checked)}
            >
              Sync &apos;Price Levels&apos; from POS
            </Checkbox>
          )}
          <Checkbox
            checked={Boolean(localSettings.inject_orders)}
            onChange={(event) => setField("inject_orders", event.target.checked)}
          >
            Inject orders into POS
          </Checkbox>
          {localSettings.inject_orders && (
            <>
              <FormRow>
                <h4>Action on Omnivore POS outage or injection failure?</h4>
                <Radio.Group
                  value={localSettings.action_on_failure}
                  onChange={(event) => setField("action_on_failure", event.target.value)}
                >
                  <Space direction="vertical">
                    {ACTION_ON_FAILURE_OPTIONS.map((option) => (
                      <Radio key={option.value} value={option.value}>
                        {option.label}
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
              </FormRow>
              <FormRow>
                <h4>Action on successful injection in {displayName}?</h4>
                <Radio.Group
                  value={localSettings.action_on_success}
                  onChange={(event) => setField("action_on_success", event.target.value)}
                >
                  <Space direction="vertical">
                    {ACTION_ON_SUCCESS_OPTIONS.map((option) => (
                      <Radio key={option.value} value={option.value}>
                        {option.label}
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
              </FormRow>
            </>
          )}
          <Checkbox
            checked={Boolean(localSettings.webhook_auto_refund)}
            onChange={(event) => setField("webhook_auto_refund", event.target.checked)}
          >
            Auto-refund items that were refunded in POS
          </Checkbox>
          {isAdmin && variant === "direct" && (
            <FormRow>
              <Space>
                <BbotButton onClick={onDownloadPosItems}>See POS Items</BbotButton>
                <BbotButton onClick={onCheckDiagnostic}>Check Menu Diagnostic</BbotButton>
              </Space>
            </FormRow>
          )}
        </div>
      )}
      <DisconnectIntegrationDialog
        visible={disconnectVisible}
        integrationName={displayName}
        onClose={() => setDisconnectVisible(false)}
        onConfirm={async (deleteMenuItems) => {
          const succeeded = await onDisconnect(deleteMenuItems);
          if (succeeded) onClose();
          return succeeded;
        }}
      />
    </BbotModal>
  );
};

OmnivoreConnectDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  variant: PropTypes.oneOf(["direct", "omnivore3"]).isRequired,
  isAdmin: PropTypes.bool.isRequired,
  settings: PropTypes.shape({
    location_id: PropTypes.string,
    employee_id: PropTypes.string,
    revenue_center_id: PropTypes.string,
    order_type_id: PropTypes.string,
    tender_type_id: PropTypes.string,
    sync_in_stock: PropTypes.bool,
    sync_price_levels: PropTypes.bool,
    inject_orders: PropTypes.bool,
    action_on_failure: PropTypes.string,
    action_on_success: PropTypes.string,
    webhook_auto_refund: PropTypes.bool,
  }),
  locationOptions: PropTypes.shape({
    employees: PropTypes.array,
    revenue_centers: PropTypes.array,
    order_types: PropTypes.array,
    tender_types: PropTypes.array,
  }),
  isLoadingLocationOptions: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onLoad: PropTypes.func.isRequired,
  onLocationIdChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDisconnect: PropTypes.func.isRequired,
  onCheckDiagnostic: PropTypes.func,
  onDownloadPosItems: PropTypes.func,
};

OmnivoreConnectDialog.defaultProps = {
  settings: null,
  locationOptions: null,
  onCheckDiagnostic: () => {},
  onDownloadPosItems: () => {},
};

export default OmnivoreConnectDialog;
